import { LinkOutlined } from '@ant-design/icons';
import type {
  Settings as LayoutSettings,
  MenuDataItem,
} from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { history, Link } from '@umijs/max';
import { AvatarDropdown, AvatarName, Footer } from '@/components';
import { createBrandHeaderRender } from '@/components/BrandLogo';
import TableTextSelectionGuard from '@/components/TableTextSelectionGuard';
import { currentUser as queryCurrentUser } from '@/services/ant-design-pro/api';
import {
  CHANGE_PASSWORD_PATH,
  loginDestination,
} from '@/services/authPassword';
import { loginPathForPage } from '@/utils/loginReturn';
import {
  bootstrapWecomPaymentLogin,
  clearWecomPaymentSession,
  isCommonLoginJumpEntry,
  isWecomPaymentEntry,
  type WecomPaymentAuth,
} from '@/utils/wecomPaymentAuth';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';
import {
  findFirstAccessibleMenuPath,
  getAccessibleMenuData,
} from './utils/menuAccess';
import '@ant-design/v5-patch-for-react-19';

const isDev = process.env.NODE_ENV === 'development';
const loginPath = '/user/login';
const loginPathForCurrentPage = () => {
  const { pathname, search } = history.location;
  return loginPathForPage(pathname, search);
};

let firstAccessibleMenuPath: string | undefined;
let rootMenuData: MenuDataItem[] = [];

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
  wecomPaymentAuth?: WecomPaymentAuth;
}> {
  const fetchUserInfo = async () => {
    try {
      const msg = await queryCurrentUser({
        skipErrorHandler: true,
      });
      if (
        msg.data.mustChangePassword &&
        history.location.pathname !== CHANGE_PASSWORD_PATH
      ) {
        history.replace(
          loginDestination(
            true,
            `${history.location.pathname}${history.location.search}`,
          ),
        );
      }
      return msg.data;
    } catch (_error) {
      localStorage.removeItem('token');
      if (isWecomPaymentEntry()) clearWecomPaymentSession();
      else history.push(loginPathForCurrentPage());
    }
    return undefined;
  };
  // 如果不是登录页面，执行
  const { location } = history;
  if (isWecomPaymentEntry()) {
    const wecomPaymentAuth = await bootstrapWecomPaymentLogin();
    const currentUser =
      wecomPaymentAuth.status === 'ready' ? await fetchUserInfo() : undefined;
    return {
      fetchUserInfo,
      currentUser,
      wecomPaymentAuth,
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }
  const isMarketIntentPortal = location.pathname.startsWith(
    '/market-intent/portal/',
  );
  if (
    ![loginPath, '/user/register', '/user/register-result'].includes(
      location.pathname,
    ) &&
    !isMarketIntentPortal &&
    !isCommonLoginJumpEntry()
  ) {
    const currentUser = await fetchUserInfo();
    return {
      fetchUserInfo,
      currentUser,
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }
  return {
    fetchUserInfo,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  const brandHeader = createBrandHeaderRender(
    defaultSettings.logo || '/logo.png',
    initialState?.settings?.title || defaultSettings.title || '新十年',
  );
  return {
    actionsRender: () => [],
    avatarProps: {
      src: initialState?.currentUser?.avatar,
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
      },
    },
    waterMarkProps: {
      content: initialState?.currentUser?.name,
    },
    footerRender: () => <Footer />,
    menuDataRender: (menuData) => {
      rootMenuData = getAccessibleMenuData(menuData);
      firstAccessibleMenuPath = findFirstAccessibleMenuPath(rootMenuData);
      return getAccessibleMenuData(menuData, true);
    },
    onPageChange: () => {
      const { location } = history;
      // 如果没有登录，重定向到 login
      if (
        !initialState?.currentUser &&
        location.pathname !== loginPath &&
        !isWecomPaymentEntry() &&
        !isCommonLoginJumpEntry()
      ) {
        history.push(loginPathForCurrentPage());
        return;
      }

      if (
        initialState?.currentUser?.mustChangePassword &&
        location.pathname !== CHANGE_PASSWORD_PATH
      ) {
        history.replace(
          loginDestination(true, `${location.pathname}${location.search}`),
        );
        return;
      }

      if (location.pathname === '/' && firstAccessibleMenuPath) {
        history.replace(firstAccessibleMenuPath);
        return;
      }

      const rootMenu = rootMenuData.find(
        (menuItem) => menuItem.path === location.pathname,
      );
      if (rootMenu) {
        const firstChildPath = findFirstAccessibleMenuPath(
          rootMenu.children ?? [],
        );
        if (firstChildPath) {
          history.replace(firstChildPath);
        }
      }
    },
    bgLayoutImgList: [
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr',
        left: 85,
        bottom: 100,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr',
        bottom: -68,
        right: -45,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr',
        bottom: 0,
        left: 0,
        width: '331px',
      },
    ],
    links: isDev
      ? [
          <Link key="openapi" to="/umi/plugin/openapi" target="_blank">
            <LinkOutlined />
            <span>OpenAPI 文档</span>
          </Link>,
        ]
      : [],
    ...brandHeader,
    // 自定义 403 页面
    // unAccessible: <div>unAccessible</div>,
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      // if (initialState?.loading) return <PageLoading />;
      return (
        <>
          <TableTextSelectionGuard>
            {initialState?.currentUser?.mustChangePassword &&
            history.location.pathname !== CHANGE_PASSWORD_PATH
              ? null
              : children}
          </TableTextSelectionGuard>
          {isDev && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((preInitialState) => ({
                  ...preInitialState,
                  settings,
                }));
              }}
            />
          )}
        </>
      );
    },
    ...initialState?.settings,
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request: RequestConfig = {
  baseURL: '/',
  ...errorConfig,
};
